import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import StatsBar from "../components/StatsBar";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Vault,
  Plus,
  Upload,
  ArrowRight,
  Trash2,
  Edit,
  MoveRight,
  Star,
  Download,
} from "lucide-react";
import ViewToggle, { gridCols } from "../components/ViewToggle";
import useViewPreference from "../hooks/useViewPreference";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bank() {
  const { user } = useAuth();
  const [vaults, setVaults] = useState([]);
  const [images, setImages] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Vault dialogs
  const [createVaultOpen, setCreateVaultOpen] = useState(false);
  const [editVaultOpen, setEditVaultOpen] = useState(false);
  const [editingVault, setEditingVault] = useState(null);
  const [vaultName, setVaultName] = useState("");
  const [vaultDesc, setVaultDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadVaultId, setUploadVaultId] = useState("none");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null); // null | { done, total }
  const fileInputRef = useRef(null);

  // Image detail
  const [selectedImage, setSelectedImage] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  // Move
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveDestination, setMoveDestination] = useState("main");
  const [moveCollectionId, setMoveCollectionId] = useState("none");
  const [moveVaultId, setMoveVaultId] = useState("none");
  const [moving, setMoving] = useState(false);

  // Quick vault assign
  const [vaultPickerImg, setVaultPickerImg] = useState(null);
  const [assigningVault, setAssigningVault] = useState(false);

  // View
  const { viewMode, setViewMode, imageSize, setImageSize } = useViewPreference("bank");

  const fetchAll = async () => {
    try {
      const [vaultsRes, imagesRes, collectionsRes] = await Promise.all([
        axios.get(`${API}/vaults`),
        axios.get(`${API}/bank`),
        axios.get(`${API}/collections`),
      ]);
      setVaults(vaultsRes.data);
      setImages(imagesRes.data);
      setCollections(collectionsRes.data);
    } catch {
      toast.error("Failed to load Bank");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ---- Vaults ----
  const handleCreateVault = async (e) => {
    e.preventDefault();
    if (!vaultName.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/vaults`, { name: vaultName, description: vaultDesc });
      toast.success("Vault created");
      setCreateVaultOpen(false);
      setVaultName(""); setVaultDesc("");
      fetchAll();
    } catch { toast.error("Failed to create vault"); }
    finally { setSubmitting(false); }
  };

  const handleUpdateVault = async (e) => {
    e.preventDefault();
    if (!vaultName.trim() || !editingVault) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/vaults/${editingVault.id}`, { name: vaultName, description: vaultDesc });
      toast.success("Vault updated");
      setEditVaultOpen(false);
      setEditingVault(null); setVaultName(""); setVaultDesc("");
      fetchAll();
    } catch { toast.error("Failed to update vault"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteVault = async (vault) => {
    if (!window.confirm(`Delete vault "${vault.name}"? Images inside will stay in Bank.`)) return;
    try {
      await axios.delete(`${API}/vaults/${vault.id}`);
      toast.success("Vault deleted");
      fetchAll();
    } catch { toast.error("Failed to delete vault"); }
  };

  const openEditVault = (vault) => {
    setEditingVault(vault);
    setVaultName(vault.name);
    setVaultDesc(vault.description || "");
    setEditVaultOpen(true);
  };

  // ---- Upload ----
  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setUploadFiles(files);
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setUploadProgress({ done: 0, total: uploadFiles.length });
    let failed = 0;
    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      try {
        const data = await readFileAsDataURL(file);
        await axios.post(`${API}/bank/upload`, {
          image_data: data,
          title: file.name,
          original_filename: file.name,
          vault_id: uploadVaultId === "none" ? null : uploadVaultId,
        });
      } catch {
        failed++;
      }
      setUploadProgress({ done: i + 1, total: uploadFiles.length });
    }
    if (failed === 0) {
      toast.success(`Uploaded ${uploadFiles.length} image${uploadFiles.length > 1 ? "s" : ""} to Bank`);
    } else {
      toast.error(`${failed} of ${uploadFiles.length} uploads failed`);
    }
    setUploadOpen(false);
    setUploadFiles([]);
    setUploadVaultId("none");
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    fetchAll();
  };

  // ---- Image Detail ----
  const openDetail = (img) => {
    setSelectedImage(img);
    setNewTitle(img.title || img.prompt || "");
    setEditingTitle(false);
    setComment("");
    setDetailOpen(true);
  };

  const refreshImages = async () => {
    const [vaultsRes, imagesRes] = await Promise.all([
      axios.get(`${API}/vaults`),
      axios.get(`${API}/bank`),
    ]);
    setVaults(vaultsRes.data);
    setImages(imagesRes.data);
    if (selectedImage) {
      const updated = imagesRes.data.find(i => i.id === selectedImage.id);
      if (updated) setSelectedImage(updated);
    }
  };

  const updateTitle = async () => {
    if (!selectedImage || !newTitle.trim()) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { title: newTitle.trim() });
      toast.success("Title updated");
      setEditingTitle(false);
      refreshImages();
    } catch { toast.error("Failed to update title"); }
  };

  const updateRating = async (rating) => {
    if (!selectedImage) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { rating });
      toast.success("Rating updated");
      refreshImages();
    } catch { toast.error("Failed to update rating"); }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim() || !selectedImage) return;
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/images/${selectedImage.id}/comments`, { text: comment });
      toast.success("Comment added");
      setComment("");
      refreshImages();
    } catch { toast.error("Failed to add comment"); }
    finally { setSubmittingComment(false); }
  };

  const handleDownload = () => {
    if (!selectedImage) return;
    const link = document.createElement("a");
    link.href = selectedImage.image_data;
    link.download = `bank-image-${selectedImage.id}.png`;
    link.click();
  };

  const handleDelete = async (img) => {
    if (!window.confirm("Delete this image from Bank? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/images/${img.id}`);
      toast.success("Image deleted");
      setDetailOpen(false);
      fetchAll();
    } catch { toast.error("Failed to delete image"); }
  };

  // ---- Move ----
  const openMove = (img) => {
    setSelectedImage(img);
    setMoveDestination("main");
    setMoveCollectionId("none");
    setMoveVaultId("none");
    setMoveOpen(true);
  };

  const handleMove = async () => {
    if (!selectedImage) return;
    setMoving(true);
    try {
      await axios.post(`${API}/images/${selectedImage.id}/move`, {
        destination: moveDestination,
        collection_id: moveDestination === "main" && moveCollectionId !== "none" ? moveCollectionId : null,
        vault_id: moveDestination === "bank" && moveVaultId !== "none" ? moveVaultId : null,
      });
      toast.success(moveDestination === "main" ? "Copied to Winners — original stays in Bank" : "Moved to Vault");
      setMoveOpen(false);
      setDetailOpen(false);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail ?? "Failed to move image"); }
    finally { setMoving(false); }
  };

  const handleAssignVault = async (img, vaultId) => {
    setAssigningVault(true);
    try {
      await axios.post(`${API}/images/${img.id}/move`, {
        destination: "bank",
        vault_id: vaultId === "none" ? null : vaultId,
      });
      const vault = vaults.find(v => v.id === vaultId);
      toast.success(vaultId === "none" ? "Removed from vault" : `Moved to ${vault?.name}`);
      setVaultPickerImg(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Failed to assign vault");
    } finally {
      setAssigningVault(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full spinner" />
    </div>
  );

  return (
    <div className="pb-20 md:pb-0" onClick={() => setVaultPickerImg(null)}>
      <StatsBar />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Bank</h1>
          <p className="text-gray-500 text-sm mt-1">Storage for all your designs and artwork</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setCreateVaultOpen(true)}
          >
            <Plus size={16} className="mr-2" />
            New Vault
          </Button>
          <Button
            className="rounded-none bg-black text-white hover:bg-gray-800"
            onClick={() => setUploadOpen(true)}
          >
            <Upload size={16} className="mr-2" />
            Upload
          </Button>
        </div>
      </div>

      {/* Vaults */}
      {vaults.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading font-bold text-sm uppercase tracking-widest text-gray-500 mb-3">Vaults</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vaults.map((vault) => (
              <div key={vault.id} className="border border-gray-200 hover:border-black transition-colors group">
                <Link to={`/bank/vaults/${vault.id}`} className="block p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-black text-white flex items-center justify-center">
                      <Vault size={18} />
                    </div>
                    <ArrowRight className="text-gray-400 group-hover:text-black transition-colors" size={18} />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-1 truncate">{vault.name}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-3">{vault.description || "No description"}</p>
                  <p className="text-xs uppercase tracking-widest text-gray-400">
                    {vault.image_count} {vault.image_count === 1 ? "image" : "images"}
                  </p>
                </Link>
                <div className="flex border-t border-gray-200">
                  <button
                    onClick={() => openEditVault(vault)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-gray-600 hover:bg-gray-50 border-r border-gray-200"
                  >
                    <Edit size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteVault(vault)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Bank Images */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-sm uppercase tracking-widest text-gray-500">
            All Bank Images ({images.length})
          </h2>
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} imageSize={imageSize} setImageSize={setImageSize} />
        </div>

        {images.length === 0 ? (
          <div className="border border-dashed border-gray-300 p-8 text-center">
            <Upload className="mx-auto text-gray-400 mb-3" size={32} />
            <p className="text-gray-500 text-sm">No images in Bank yet</p>
            <Button onClick={() => setUploadOpen(true)} variant="outline" className="mt-4 rounded-none">
              Upload your first image
            </Button>
          </div>
        ) : viewMode === "list" ? (
          <div className="border border-gray-200">
            {images.map((img, i) => (
              <div key={img.id} className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${i % 2 === 0 ? "" : "bg-gray-50/50"}`} onClick={() => openDetail(img)}>
                <img src={img.image_data} alt={img.title || img.prompt} className="w-10 h-10 object-cover flex-shrink-0 border border-gray-200" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate font-medium">{img.title || img.prompt}</p>
                  {img.vault_id && <p className="text-xs text-gray-400">{vaults.find(v => v.id === img.vault_id)?.name}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                    <button onClick={() => setVaultPickerImg(vaultPickerImg?.id === img.id ? null : img)} className={`p-1.5 transition-colors ${img.vault_id ? "text-black" : "text-gray-400 hover:text-black"}`} title="Assign to Vault"><Vault size={13} /></button>
                    {vaultPickerImg?.id === img.id && (
                      <div className="absolute right-0 z-20 bg-white border border-gray-200 shadow-md min-w-[140px] py-1">
                        {vaults.map((v) => (
                          <button key={v.id} onClick={() => handleAssignVault(img, v.id)} disabled={assigningVault} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${img.vault_id === v.id ? "font-bold" : ""}`}>{img.vault_id === v.id ? "✓ " : ""}{v.name}</button>
                        ))}
                        {img.vault_id && <button onClick={() => handleAssignVault(img, "none")} disabled={assigningVault} className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border-t border-gray-100">Remove from vault</button>}
                      </div>
                    )}
                  </div>
                  <button onClick={() => openMove(img)} className="p-1.5 text-gray-400 hover:text-black transition-colors" title="Copy to Winners"><MoveRight size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`grid gap-3 ${gridCols(imageSize)}`}>
            {images.map((img) => (
              <div key={img.id} className="border border-gray-200 hover:border-black transition-colors group relative">
                <div
                  className="aspect-square bg-gray-100 cursor-pointer"
                  onClick={() => openDetail(img)}
                >
                  <img src={img.image_data} alt={img.title || img.prompt} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-2 border-t border-gray-200">
                  <p className="text-xs truncate text-gray-700 mb-1">{img.title || img.prompt}</p>
                  <div className="flex items-center justify-between">
                    {img.vault_id ? (
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider truncate max-w-[80px]">
                        {vaults.find(v => v.id === img.vault_id)?.name || "Vault"}
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-300 uppercase tracking-wider">Unvaulted</span>
                    )}
                    <div className="flex items-center gap-1">
                      <div className="relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); setVaultPickerImg(vaultPickerImg?.id === img.id ? null : img); }}
                          className={`transition-colors ${img.vault_id ? "text-black" : "text-gray-400 hover:text-black"}`}
                          title="Assign to Vault"
                        >
                          <Vault size={13} />
                        </button>
                        {vaultPickerImg?.id === img.id && (
                          <div className="absolute bottom-6 right-0 z-20 bg-white border border-gray-200 shadow-md min-w-[140px] py-1">
                            <p className="px-3 py-1 text-[10px] uppercase tracking-widest text-gray-400">Assign to vault</p>
                            {vaults.map((v) => (
                              <button
                                key={v.id}
                                onClick={(e) => { e.stopPropagation(); handleAssignVault(img, v.id); }}
                                disabled={assigningVault}
                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${img.vault_id === v.id ? "font-bold" : ""}`}
                              >
                                {img.vault_id === v.id ? "✓ " : ""}{v.name}
                              </button>
                            ))}
                            {img.vault_id && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAssignVault(img, "none"); }}
                                disabled={assigningVault}
                                className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border-t border-gray-100"
                              >
                                Remove from vault
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => openMove(img)}
                        className="text-gray-400 hover:text-black transition-colors"
                        title="Copy to Winners"
                      >
                        <MoveRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Vault Dialog */}
      <Dialog open={createVaultOpen} onOpenChange={(o) => { setCreateVaultOpen(o); if (!o) { setVaultName(""); setVaultDesc(""); } }}>
        <DialogContent className="rounded-none bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Create Vault</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateVault} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
              <Input value={vaultName} onChange={(e) => setVaultName(e.target.value)} placeholder="Vault name" className="rounded-none border-gray-300 bg-white text-black" required autoFocus />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Description</Label>
              <Textarea value={vaultDesc} onChange={(e) => setVaultDesc(e.target.value)} placeholder="Optional description" className="rounded-none border-gray-300 bg-white text-black resize-none" rows={3} />
            </div>
            <Button type="submit" disabled={submitting} className="w-full rounded-none bg-black text-white hover:bg-gray-800">
              {submitting ? "Saving..." : "Create Vault"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vault Dialog */}
      <Dialog open={editVaultOpen} onOpenChange={(o) => { setEditVaultOpen(o); if (!o) { setEditingVault(null); setVaultName(""); setVaultDesc(""); } }}>
        <DialogContent className="rounded-none bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Edit Vault</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateVault} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
              <Input value={vaultName} onChange={(e) => setVaultName(e.target.value)} placeholder="Vault name" className="rounded-none border-gray-300 bg-white text-black" required autoFocus />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Description</Label>
              <Textarea value={vaultDesc} onChange={(e) => setVaultDesc(e.target.value)} placeholder="Optional description" className="rounded-none border-gray-300 bg-white text-black resize-none" rows={3} />
            </div>
            <Button type="submit" disabled={submitting} className="w-full rounded-none bg-black text-white hover:bg-gray-800">
              {submitting ? "Saving..." : "Update Vault"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => {
        if (uploadProgress) return; // block close while uploading
        setUploadOpen(o);
        if (!o) { setUploadFiles([]); setUploadVaultId("none"); if (fileInputRef.current) fileInputRef.current.value = ""; }
      }}>
        <DialogContent className="rounded-none bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Upload to Bank</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Add to Vault (optional)</Label>
              <Select value={uploadVaultId} onValueChange={setUploadVaultId}>
                <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                  <SelectValue placeholder="No vault" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No vault</SelectItem>
                  {vaults.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFilesSelected} />

            {/* File selection area */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!!uploadProgress}
              className="w-full border-2 border-dashed border-gray-300 hover:border-black transition-colors p-6 text-center"
            >
              <Upload size={20} className="mx-auto text-gray-400 mb-2" />
              {uploadFiles.length > 0 ? (
                <p className="text-sm font-medium text-black">{uploadFiles.length} file{uploadFiles.length > 1 ? "s" : ""} selected</p>
              ) : (
                <p className="text-sm text-gray-500">Click to choose images</p>
              )}
              <p className="text-xs text-gray-400 mt-1">Select multiple files at once</p>
            </button>

            {/* Selected file list */}
            {uploadFiles.length > 0 && !uploadProgress && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {uploadFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1">
                    <span className="truncate">{f.name}</span>
                    <span className="text-gray-400 ml-2 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
              </div>
            )}

            {/* Progress */}
            {uploadProgress && (
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Uploading…</span>
                  <span>{uploadProgress.done} / {uploadProgress.total}</span>
                </div>
                <div className="h-1.5 bg-gray-200">
                  <div
                    className="h-1.5 bg-black transition-all duration-300"
                    style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={!uploadFiles.length || !!uploadProgress}
              className="w-full rounded-none bg-black text-white hover:bg-gray-800"
            >
              <Upload size={16} className="mr-2" />
              {uploadProgress ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : `Upload ${uploadFiles.length || ""} Image${uploadFiles.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Bank Image</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              <img src={selectedImage.image_data} alt={selectedImage.title} className="w-full border border-gray-200" />
              <div className="flex gap-2">
                <Button onClick={handleDownload} variant="outline" size="sm" className="rounded-none flex-1">
                  <Download size={14} className="mr-2" /> Download
                </Button>
                <Button onClick={() => { setDetailOpen(false); openMove(selectedImage); }} size="sm" className="rounded-none flex-1 bg-black text-white hover:bg-gray-800">
                  <MoveRight size={14} className="mr-2" /> Copy to Winners
                </Button>
                {(user?.role === "admin" || user?.can_delete_bank) && (
                  <Button onClick={() => handleDelete(selectedImage)} variant="outline" size="sm" className="rounded-none text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100">
                {/* Title */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase tracking-widest text-gray-500">Title</p>
                    {!editingTitle && <button onClick={() => setEditingTitle(true)} className="text-xs text-gray-500 hover:text-black underline">Edit</button>}
                  </div>
                  {editingTitle ? (
                    <div className="flex gap-2">
                      <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="flex-1 px-2 py-1 text-sm border border-gray-300 bg-white text-black" autoFocus />
                      <Button onClick={updateTitle} size="sm" className="rounded-none bg-black text-white px-3">Save</Button>
                      <Button onClick={() => { setEditingTitle(false); setNewTitle(selectedImage.title || selectedImage.prompt || ""); }} size="sm" variant="outline" className="rounded-none px-3">Cancel</Button>
                    </div>
                  ) : (
                    <p className="text-sm">{selectedImage.title || selectedImage.prompt || "-"}</p>
                  )}
                </div>

                {/* Uploaded */}
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Uploaded</p>
                  <p className="text-sm text-gray-700">
                    {selectedImage.created_at
                      ? new Date(selectedImage.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </p>
                </div>

                {/* Rating */}
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Rating</p>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map((star) => (
                      <button key={star} onClick={() => updateRating(star)} className="p-0.5">
                        <Star size={18} className={star <= (selectedImage.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comments */}
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                    Comments {selectedImage.comments?.length > 0 && `(${selectedImage.comments.length})`}
                  </p>
                  {selectedImage.comments?.length > 0 && (
                    <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                      {selectedImage.comments.map((c, i) => (
                        <div key={i} className="p-2 bg-gray-50 border border-gray-200 text-sm">
                          <p className="font-medium text-xs text-black">{c.user_name}</p>
                          <p className="text-gray-600">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <form onSubmit={addComment} className="flex gap-2">
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." className="rounded-none border-gray-300 bg-white text-black resize-none text-sm" rows={2} />
                    <Button type="submit" disabled={submittingComment || !comment.trim()} className="rounded-none bg-black text-white hover:bg-gray-800 self-end">
                      {submittingComment ? "..." : "Add"}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="rounded-none bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Copy / Move Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Action</Label>
              <Select value={moveDestination} onValueChange={setMoveDestination}>
                <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Copy to Winners (stays in Bank)</SelectItem>
                  <SelectItem value="bank">Move to different Vault</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {moveDestination === "main" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Collection (optional)</Label>
                <Select value={moveCollectionId} onValueChange={setMoveCollectionId}>
                  <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                    <SelectValue placeholder="No collection" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No collection</SelectItem>
                    {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {moveDestination === "bank" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Vault (optional)</Label>
                <Select value={moveVaultId} onValueChange={setMoveVaultId}>
                  <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                    <SelectValue placeholder="No vault" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vault</SelectItem>
                    {vaults.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button onClick={handleMove} disabled={moving} className="w-full rounded-none bg-black text-white hover:bg-gray-800">
              <MoveRight size={16} className="mr-2" />
              {moving ? "Working..." : moveDestination === "main" ? "Copy to Winners" : "Move to Vault"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
